import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import BaseTeamPage from './BaseTeamPage';
import TeamInputField from './TeamInputField';
import { Avatar } from 'src/game/components/shared/Avatar';
import sounds from 'src/lib/sounds';
import { withLoading } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { profileAvatarAssets } from 'src/replicant/creatives/assets';
import StateObserver, { user } from 'src/StateObserver';
import { MagicLampsCostButton } from '../../magiclamps/MagicLampsCostButton';
import TeamNumberField from './TeamNumberField';
import { getMatch2Level } from 'src/replicant/match2/match2.getters';
import { TEAM_CREATION_COST } from 'src/replicant/team/team.ruleset';
import * as Inventory from 'src/replicant/inventory';
import { startGemsPinchSequence } from 'src/game/shop/Helpers';
import { track } from 'src/lib/analytics/utils';

const ui = uiConfig.buruburu.care.confirmation;

export default class TeamCreatePage extends BaseTeamPage {
  private name!: TeamInputField;
  private avatar!: Avatar;
  private description!: TeamInputField;
  private level!: TeamNumberField;

  private get currentAvatar() {
    return this._currentAvatar;
  }

  private set currentAvatar(avatar: string) {
    this._currentAvatar = avatar;
    this.avatar.updateImage(avatar);
  }
  private _currentAvatar: string = '';

  constructor(superview: View) {
    super(superview);
    this.addContent();
    this.addHeader();
  }

  private addHeader() {
    const width = this.header.style.width * 0.85;
    const padding = (this.header.style.width - width) * 0.5;
    new LangBitmapFontTextView({
      superview: this.header,
      font: 'Body',
      size: 34,
      color: '#76233C',
      localeText: () => i18n('match2.team.create_header_message'),
      width,
      height: this.header.style.height - 5,
      x: padding,
      align: 'center',
      verticalAlign: 'center',
    });
  }

  private addContent() {
    const panel = new ImageView({
      superview: this.contentView,
      image: 'assets/ui/match2/team/team_create_panel.png',
      width: 670,
      height: 756,
      centerOnOrigin: true,
      x: this.contentView.style.width * 0.5,
      y: 756 * 0.5 + 20,
    });

    const x = 245;
    let y = 75;

    this.addLabel(panel, 'match2.team.create_team_name', y);
    this.name = new TeamInputField({
      superview: panel,
      x,
      y: y + 17,
      placeholder: 'match2.team.create_placeholder',
      minLength: 3,
      maxLength: 20,
    });

    y += 105;
    const size = 84;
    this.addLabel(panel, 'match2.team.create_badge', y);
    this.avatar = new Avatar({
      superview: panel,
      width: size,
      height: size,
      x: x + size * 0.5,
      y: y + 17,
    });
    new ButtonScaleViewWithText({
      ...ui.okButton,
      fontSize: 38,
      width: 181,
      height: 75,
      x: this.avatar.style.x + size * 0.5 + 30,
      y: y + 17 - 75 * 0.5,
      superview: panel,
      localeText: () => i18n('match2.team.create_browse'),
      onClick: async () => {
        sounds.sfx.play('ok');
        const currentAvatar = await openPopupPromise('popupAvatarSelection', {
          currentAvatar: this.currentAvatar,
        });
        this.currentAvatar = currentAvatar;
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CREATE_BROWSE,
      },
    });

    y += 105;
    this.addLabel(panel, 'match2.team.create_description', y);
    this.description = new TeamInputField({
      superview: panel,
      x,
      y: y + 17,
      height: 218,
      placeholder: 'match2.team.create_placeholder2',
      multiline: true,
      minLength: 0,
      maxLength: 50,
    });

    y += 250;
    this.addLabel(panel, 'match2.team.create_required_level', y);
    this.level = new TeamNumberField({
      superview: panel,
      x,
      y: y + 14,
      min: 0,
      max: getMatch2Level(user(), false),
      increment: 50,
    });

    const cta = new MagicLampsCostButton({
      superview: this.contentView,
      onClick: async () => {
        sounds.sfx.play('ok');
        if (Inventory.getters.getGems(user()) < TEAM_CREATION_COST) {
          await startGemsPinchSequence({
            feature: Feature.MATCH2.TEAM.CREATE,
            crossplayTracking: {
              feature: 'team',
              subFeature: 'team_create',
            },
          });
          return;
        }

        const id = await withLoading(
          () =>
            StateObserver.invoke.asyncCreateTeam({
              name: this.name.text,
              description: this.description.text,
              icon: this._currentAvatar,
              levelRequired: this.level.value,
            }),
          {
            feature: Feature.TEAM._,
            subFeature: Feature.TEAM.CREATE_TEAM,
          },
        );

        track('TeamCreate', {
          id,
          name: this.name.text,
          description: this.description.text,
          icon: this._currentAvatar,
          levelRequired: this.level.value,
        });

        this.emit('Close');

        void openPopupPromise('popupTeam', {});
      },
      centerOnOrigin: true,
      x: this.contentView.style.width * 0.5,
      y: panel.style.y + panel.style.height * 0.5 + 65,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CREATE_CREATE,
      },
    });
    cta.setup({
      i18n: 'match2.team.create_cta',
      gems: TEAM_CREATION_COST,
    });
    cta.show();
    cta.setDisabled(true);

    // Only enable CTA when both input fields are valid.
    let validName = false;
    let validDescription = true;

    this.name.inputField.on('InputValidated', (valid: boolean) => {
      validName = valid;
      cta.setDisabled(!validName || !validDescription);
    });
    this.description.inputField.on('InputValidated', (valid: boolean) => {
      validDescription = valid;
      cta.setDisabled(!validName || !validDescription);
    });

    this.name.inputField.attachToPopup('popupTeam');
    this.description.inputField.attachToPopup('popupTeam');
  }

  private addLabel(superview: View, label: string, y: number) {
    return new LangBitmapFontTextView({
      superview,
      font: 'Body',
      color: '#F24463',
      size: 28,
      localeText: () => i18n(label),
      align: 'right',
      x: 20,
      y,
      width: 195,
      height: 100,
    });
  }

  public override refresh() {
    if (!this.currentAvatar.length) {
      this.currentAvatar = getRandomItemFromArray(
        Object.keys(profileAvatarAssets).map((id) =>
          StateObserver.replicant.getChatbotAssetUrl(id),
        ),
        Math.random,
      );
    }
  }
}
